import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider
} from '@mui/material';
import { WarningAmber, CheckCircle } from '@mui/icons-material';
import dayjs from 'dayjs';
import { getStatusColor, getPriorityColor } from '../utils/maintenanceUtils';
import { getWorkOrderDisplayNumber } from '../../../utils/workOrderDisplayNumber';
import { formatDateTimeWithTimezone } from '../../../utils/timezoneUtils';
import CamoQmAcknowledgmentForm from '../CamoQmAcknowledgmentForm';
import CamoQmCompletionAcknowledgmentForm from '../CamoQmCompletionAcknowledgmentForm';
import { getMaintenanceWorkOrderTypeLabel as getTypeLabel } from '../../../constants/maintenanceWorkOrderTypes';

const SummaryTable = ({ title, headers, rows, emptyMessage }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
      {title} {rows.length > 0 && `(${rows.length})`}
    </Typography>
    {rows.length === 0 ? (
      <Typography variant="body2" color="text.secondary">{emptyMessage}</Typography>
    ) : (
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableCell key={header}>{header}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Box>
);

const DetailField = ({ label, children }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body2">{children ?? '—'}</Typography>
  </Grid>
);

/**
 * Full work order review surface for CAMO QM before acknowledgment.
 */
const CamoQmReviewTab = ({
  workOrder,
  displayTimezone,
  canAcknowledge,
  canCAMOQMCompletionAck,
  onSuccess
}) => {
  if (!workOrder) return null;

  const needsCreationAck = canAcknowledge?.(workOrder);
  const needsCompletionAck = canCAMOQMCompletionAck?.(workOrder);
  const actionRequired = needsCreationAck || needsCompletionAck;

  const formatDate = (value) => {
    if (!value) return '—';
    if (displayTimezone) {
      return formatDateTimeWithTimezone(value, null, displayTimezone) || '—';
    }
    return dayjs(value).format('YYYY-MM-DD HH:mm');
  };

  const ampCheckRows = (workOrder.ampChecks || []).map((check) => [
    check.sequenceLabel || check.sequenceNumber,
    check.description || '—',
    check.taskReferenceId || '—',
    check.status || 'PENDING'
  ]);

  const spareRows = (workOrder.requiredSpares || []).map((item) => [
    item.partNumber || '—',
    item.quantity ?? '—',
    item.itemName || '—',
    item.taskReference || '—'
  ]);

  const consumableRows = (workOrder.requiredConsumables || []).map((item) => [
    item.partNumberOrSpecification || '—',
    item.quantity ?? '—',
    item.itemName || '—',
    item.expiry ? dayjs(item.expiry).format('YYYY-MM-DD') : '—'
  ]);

  const toolRows = (workOrder.requiredToolsEquipment || []).map((item) => [
    item.partNumber || '—',
    item.itemName || '—',
    item.due ? dayjs(item.due).format('YYYY-MM-DD') : '—',
    item.taskReferenceId || '—'
  ]);

  const verificationRows = (workOrder.verificationChecks || []).map((item) => [
    item.task || '—',
    item.status || 'PENDING'
  ]);

  const procurement = workOrder.partsProcurementRequirements;
  const procurementRows = (procurement?.partNumbers || []).map((pn, index) => [
    pn,
    procurement.quantities?.[index] ?? '—',
    procurement.descriptions?.[index] || '—'
  ]);

  const assemblyLabels = (workOrder.assemblies || [])
    .map((assembly) => {
      if (!assembly) return null;
      if (typeof assembly === 'string') return assembly;
      const parts = [assembly.typeName, assembly.serialNumber, assembly.position].filter(Boolean);
      return parts.join(' · ') || assembly._id;
    })
    .filter(Boolean);

  return (
    <Box>
      {workOrder.standaloneEmergencyRepair && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Stand-alone emergency work order. CAMO QM creation review is required the same as a normal work order
          before Planning can act. Inform Sales and Operations — future charters may be cancelled.
        </Alert>
      )}

      {actionRequired ? (
        <Alert
          severity="warning"
          icon={<WarningAmber />}
          sx={{ mb: 3, fontWeight: 500 }}
        >
          {needsCreationAck
            ? 'CAMO QM acknowledgment is required before Planning can proceed with this work order.'
            : 'CAMO QM completion acknowledgment is required before this work order can be marked complete.'}
        </Alert>
      ) : (
        <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
          No CAMO QM action is pending on this work order. This tab shows the full work order record for reference.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
        <Typography variant="h6" gutterBottom>Work Order Summary</Typography>
        <Grid container spacing={2}>
          <DetailField label="Work Order #">{getWorkOrderDisplayNumber(workOrder) || workOrder.woNumber || '—'}</DetailField>
          <DetailField label="Title">{workOrder.title || '—'}</DetailField>
          <DetailField label="Aircraft">{workOrder.aircraftId?.registration || 'Unknown'}</DetailField>
          <DetailField label="Type">{getTypeLabel(workOrder.type)}</DetailField>
          <DetailField label="Status">
            <Chip label={workOrder.status} color={getStatusColor(workOrder.status)} size="small" />
          </DetailField>
          <DetailField label="Priority">
            <Chip label={workOrder.priority} color={getPriorityColor(workOrder.priority)} size="small" />
          </DetailField>
          <DetailField label="Scheduled Date">{formatDate(workOrder.scheduledDate || workOrder.dueDate)}</DetailField>
          <DetailField label="WO Date">{formatDate(workOrder.woDate)}</DetailField>
          <DetailField label="Maintenance Type">{workOrder.maintenanceType || '—'}</DetailField>
          <DetailField label="Assemblies">
            {assemblyLabels.length ? assemblyLabels.join('; ') : '—'}
          </DetailField>
          <DetailField label="Needs Store Action">{workOrder.needsStoreAction ? 'Yes' : 'No'}</DetailField>
          <DetailField label="Needs MRO Action">{workOrder.needsMROAction ? 'Yes' : 'No'}</DetailField>
          <DetailField label="Planning Approved">{workOrder.planningApproved ? 'Yes' : 'No'}</DetailField>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" display="block">Description</Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {workOrder.description || 'No description'}
            </Typography>
          </Grid>
          {workOrder.creationSourceRemarks && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">Creation Source / Reason</Typography>
              <Typography variant="body2">{workOrder.creationSourceRemarks}</Typography>
            </Grid>
          )}
          {workOrder.planningNotes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">Planning Notes</Typography>
              <Typography variant="body2">{workOrder.planningNotes}</Typography>
            </Grid>
          )}
          {workOrder.linkedIntimationIds?.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                Linked Intimations
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {workOrder.linkedIntimationIds.map((int) => (
                  <Chip
                    key={int._id || int}
                    size="small"
                    variant="outlined"
                    label={int.title ? `${int.title} (${int.status || '–'})` : `Intimation ${String(int._id || int).slice(-6)}`}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      <Typography variant="h6" gutterBottom>Materials & Checks</Typography>

      <SummaryTable
        title="Checklist (completed by MRO Engineer)"
        headers={['S/N', 'Description', 'Task Ref', 'Status']}
        rows={ampCheckRows}
        emptyMessage="No checklist items on this work order."
      />
      <SummaryTable
        title="Required Spares"
        headers={['Part Number', 'Qty', 'Description', 'Task Ref']}
        rows={spareRows}
        emptyMessage="No required spares listed."
      />
      <SummaryTable
        title="Required Consumables"
        headers={['Part Number / Spec', 'Qty', 'Description', 'Expiry']}
        rows={consumableRows}
        emptyMessage="No required consumables listed."
      />
      <SummaryTable
        title="Tools & Equipment"
        headers={['Part Number', 'Description', 'Due', 'Task Ref']}
        rows={toolRows}
        emptyMessage="No required tools listed."
      />
      <SummaryTable
        title="Verification Checks"
        headers={['Task', 'Status']}
        rows={verificationRows}
        emptyMessage="No verification checks listed."
      />
      <SummaryTable
        title="Parts Procurement Requirements"
        headers={['Part Number', 'Qty', 'Description']}
        rows={procurementRows}
        emptyMessage="No procurement requirements submitted yet."
      />

      <Divider sx={{ my: 3 }} />

      {needsCreationAck && (
        <CamoQmAcknowledgmentForm
          workOrder={workOrder}
          onSuccess={onSuccess}
          embedded
        />
      )}

      {needsCompletionAck && (
        <CamoQmCompletionAcknowledgmentForm
          workOrder={workOrder}
          onSuccess={onSuccess}
          embedded
        />
      )}

      {!needsCreationAck && !needsCompletionAck && workOrder.camoQmAcknowledged && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>CAMO QM Status</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Creation acknowledged{workOrder.camoQmAcknowledgedAt ? ` on ${formatDate(workOrder.camoQmAcknowledgedAt)}` : ''}.
            {workOrder.camoQmNotes ? ` Notes: ${workOrder.camoQmNotes}` : ''}
          </Typography>
          {workOrder.camoQmCompletionAcknowledged && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Completion acknowledged{workOrder.camoQmCompletionAcknowledgedAt ? ` on ${formatDate(workOrder.camoQmCompletionAcknowledgedAt)}` : ''}.
            </Typography>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CamoQmReviewTab;
